import type { LiniyaHttpClient } from '../http/client';
import type {
  PasswordChangeRequest,
  RegistrationRequest,
  SessionState,
  TokenPair,
  TokenRequest,
} from '../types/auth';
import type { Device, PushRegistration } from '../types/device';
import type { User } from '../types/user';

export class AuthApi {
  constructor(private readonly http: LiniyaHttpClient) {}

  /**
   * Exchange credentials for a token pair and adopt it.
   *
   * The device description is sent so the account's device list shows
   * something a person recognises, and so a re-authenticating client can claim
   * the device it already owns instead of accumulating a new one each time.
   */
  async signIn(request: TokenRequest): Promise<TokenPair> {
    const pair = await this.http.request<TokenPair>('/api/v1/auth/token/', {
      method: 'POST',
      body: { token_delivery: this.http.delivery, ...request },
      anonymous: true,
      noRefresh: true,
    });
    await this.http.adoptTokenPair(pair);
    return pair;
  }

  async register(request: RegistrationRequest): Promise<{ user: User }> {
    return await this.http.request<{ user: User }>('/api/v1/auth/register/', {
      method: 'POST',
      body: request,
      anonymous: true,
      noRefresh: true,
    });
  }

  /**
   * Restore a session on page load.
   *
   * The access token deliberately did not survive the reload, so the only
   * thing left is the refresh credential — a cookie in the browser, keystore
   * material on a device. Exchanging it is what turns a fresh page back into a
   * signed-in one; a null result means the user has to sign in again.
   */
  async resume(): Promise<{ deviceId: string } | null> {
    const tokens = await this.http.refresh();
    return tokens ? { deviceId: tokens.deviceId } : null;
  }

  async session(): Promise<SessionState> {
    return await this.http.request<SessionState>('/api/v1/auth/session/');
  }

  /** Sign out of this device only. */
  async signOut(): Promise<void> {
    const tokens = await this.http.currentTokens();
    await this.http
      .request<void>('/api/v1/auth/token/revoke/', {
        method: 'POST',
        body: tokens?.refreshToken ? { refresh_token: tokens.refreshToken } : {},
        anonymous: true,
        noRefresh: true,
      })
      .catch(() => undefined);
    await this.http.forgetTokens();
  }

  /** Sign out of every other device, keeping this one signed in. */
  async signOutEverywhereElse(): Promise<void> {
    await this.http.request<void>('/api/v1/auth/token/revoke/', {
      method: 'POST',
      body: { all_devices: true },
    });
  }

  async changePassword(request: PasswordChangeRequest): Promise<void> {
    await this.http.request<void>('/api/v1/auth/password/change/', {
      method: 'POST',
      body: request,
    });
  }

  async registerPush(registration: PushRegistration): Promise<{ device: Device }> {
    return await this.http.request<{ device: Device }>('/api/v1/auth/push/', {
      method: 'POST',
      body: registration,
    });
  }
}
